import { Button, color } from "@funded-here-interface/common";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useStyle } from "../../styles/BorrowerDocUpload";
import { investorinddata } from "./Data";
import useCreateOrg from "../../hooks/useCreateOrg";
import {
  InvestmentMode,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import { Industry } from "../../constants/enums";
import { getCode } from "country-list";
import FileUploadComponent from "../../components/FileUpload/FileUpload";
import { Bank } from "../../types/org.type";
import {
  countryCodeAndCurrency,
  countryNameToCodeMap,
} from "@funded-here-interface/common/src/constant/country";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { File as FileEnum } from "@funded-here-interface/common/src/constant/enum";
import {
  initializeDocuments,
  initializeIndustryDetails,
  initializePersonalDetails,
  initializeAddressDetails,
  initializeBankDetails,
} from "../../Utils/dispatchIndividual";

const IndInvestorDoc: FC = () => {
  const { classes } = useStyle();
  const [isLoading, setIsLoading] = useState(false);

  // Redux state selectors
  const {
    proof: proofOfAddress,
    salaryProof,
    bank: bankStatement,
  } = useSelector((state: RootState) => state.bordoc);
  const userId = useSelector((state: RootState) => state.auth.userId);
  const dispatch = useDispatch();
  const hasFetched = useRef(false);
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();
  const createOrgmutation = useCreateOrg();
  const { token, userFirstName, userLastName } = useSelector(
    (state: RootState) => state.auth
  );
  const Navigate = useNavigate();

  const [bankData, setBank] = useState({
    bankName: "",
    accountType: "",
    accountNumber: "",
    accountHolderName: "",
    swiftCode: "",
  });

  // Fetch draft and populate redux on mount
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!hasFetched.current && !isLoading) {
        setIsLoading(true);
        try {
          const params = { userId: parseInt(userId), token };
          const data = await getOrgDraftMutation.mutateAsync(params);

          if (data && isMounted) {
            initializeDocuments(dispatch, data);
            initializeIndustryDetails(dispatch, data);
            initializePersonalDetails(dispatch, data);
            initializeAddressDetails(dispatch, data);
            initializeBankDetails(dispatch, data);

            setBank({
              bankName: data.bankName,
              accountType: data.accountType,
              accountNumber: data.accountNumber,
              accountHolderName: data.accountHolderName,
              swiftCode: data.swiftCode,
            });

            hasFetched.current = true;
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Selectors for profile and bank
  const {
    industryGroup,
    industry,
    industryOthers,
    occupation,
    experience,
    nationality,
    gender,
    employmentStatus,
    employmentPosition,
    dateOfBirth,
    countryOfBirth,
    countryOfResidence,
    address,
    postalCode,
    investmentAmount,
    phoneNumber,
    employerName,
    referralSource,
    otherReferral,
    isPep,
  } = useSelector((state: RootState) => state.prof);

  // Navigation handlers
  const handleBack = () => {
    saveDraft(false);
    Navigate("/bank-details-ind");
  };

  // Save draft handler
  const saveDraft = (showToast: boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        proofOfAddress,
        salaryProof,
        bankStatement,
      },
      {
        onSuccess: () => {
          if (showToast) toast.success("Draft Saved");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  // Submit handler
  const handleNext = async () => {
    const missingFiles = [];

    if (proofOfAddress.length === 0) missingFiles.push("proof of address file");
    if (salaryProof.length === 0) missingFiles.push("salary proof file");
    if (bankStatement.length === 0) missingFiles.push("bank statement file");

    if (missingFiles.length > 0) {
      toast.error(`Please upload ${missingFiles.join(", ")}`);

      return;
    }

    const bank: Bank = {
      bankName: bankData.bankName! as string,
      swiftCode: bankData.swiftCode! as string,
      accountNo: bankData.accountNumber! as string,
      accountType: bankData.accountType
        ? (bankData.accountType as string).toLowerCase()
        : "",
      acountHolderName: bankData.accountHolderName! as string,
    };

    const documentsUpload = {
      proofOfAddress: proofOfAddress.map((f) => f.path),
      salaryProof: salaryProof.map((f) => f.path),
      bankStatement: bankStatement.map((f) => f.path),
    };

    const investmentPreferences = {
      investmentSize: investmentAmount! as number,
      investmentMode: InvestmentMode.MANUAL,
    };

    let countryCode = null;

    if (countryOfResidence && countryOfResidence in countryCodeAndCurrency) {
      countryCode = countryNameToCodeMap.get(countryOfResidence);
    } else {
      getCode(countryOfResidence);
    }

    setIsLoading(true);
    createOrgmutation.mutate(
      {
        bank,
        token,
        companyPhoneNumber: phoneNumber! as string,
        onBoardingUserType: Persona.INVESTOR,
        nameOfAuthorisedRepresentiative: `${userFirstName} ${userLastName}`,
        occupation: occupation! as string,
        numberofYears: parseInt(experience!),
        legalEntityAddress: address! as string,
        countryISOCode: countryCode,
        countryOfIncorporation: countryOfResidence! as string,
        industryGroup: industryGroup! as string,
        industry:
          industry === Industry.OTHERS
            ? (industryOthers! as string)
            : (industry! as string),
        postalCode: parseInt(`${postalCode}`),
        nationality: nationality! as string,
        gender: gender! as string,
        employmentStatus: employmentStatus! as string,
        employmentPosition: employmentPosition! as string,
        dateOfBirth: dateOfBirth! as string,
        countryOfBirth: countryOfBirth! as string,
        isPep: isPep! as boolean,
        documentsUpload,
        investmentPreferences,
        employerName: employerName! as string,
        referralSource: referralSource! as string,
        otherReferral: otherReferral! as string,
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          Navigate("/identify-me");
        },
        onError: (e) => {
          setIsLoading(false);
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <>
      <div className={classes.topDiv}>
        <p className={classes.headinfo}>
          All uploads must be in PDF format. The size should not exceed 10 MB
          each
        </p>
      </div>

      <div
        className={classes.stepContent}
        style={{ marginTop: "0px", background: "#fff" }}
      >
        {investorinddata.map((item) => (
          <FileUploadComponent
            key={item.number}
            number={item.number}
            fileId={item.id}
            FileName={item.heading}
            existingFiles={
              item.id === FileEnum.PROOF_OF_ADDRESS
                ? proofOfAddress
                : item.id === FileEnum.SALARY_PROOF
                ? salaryProof
                : item.id === FileEnum.BANK_STATEMENT
                ? bankStatement
                : []
            }
          />
        ))}

        <div className={classes.buttonDiv2}>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={handleBack}
              backgroundColor={color.WHITE}
              textColor={color.FHGREEN}
              border="1px solid #338F86"
              width="210px"
            >
              Back
            </Button>
          </div>
          <div className={classes.buttonWrapper}>
            <Button
              disabled={isLoading}
              onClick={handleNext}
              textColor={color.WHITE}
              backgroundColor={color.FHGREEN}
              width="210px"
            >
              Submit
            </Button>
          </div>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={saveDraft}
              textColor={color.WHITE}
              backgroundColor={color.LOANTEXT}
              width="100%"
            >
              Save Draft
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default IndInvestorDoc;
